import React from 'react';
import styled from 'styled-components';
import {
  Zap,
  Shield,
  Code2,
  Terminal,
  Braces,
  Layers3,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const features: Feature[] = [
  {
    icon: <Zap size={20} strokeWidth={1.5} />,
    title: 'High Performance',
    description:
      'Optimized market data clients with efficient serialization, connection reuse, and minimal overhead for demanding research workloads.',
  },
  {
    icon: <Shield size={20} strokeWidth={1.5} />,
    title: 'Authentication & Security',
    description:
      'Built-in API key management, secure request handling, and enterprise-ready authentication workflows for your financial data API.',
  },
  {
    icon: <Code2 size={20} strokeWidth={1.5} />,
    title: 'Type Safety',
    description:
      'Strongly typed SDKs with autocomplete, request validation, and generated models, ideal for TypeScript financial API development.',
  },
  {
    icon: <Terminal size={20} strokeWidth={1.5} />,
    title: 'Quick Integration',
    description:
      'Install the financial data SDK, authenticate, and make your first request in minutes with concise APIs and practical examples.',
  },
  {
    icon: <Braces size={20} strokeWidth={1.5} />,
    title: 'Developer Experience',
    description:
      'Clear documentation, comprehensive examples, and predictable interfaces designed for productivity.',
  },
  {
    icon: <Layers3 size={20} strokeWidth={1.5} />,
    title: 'Consistent APIs',
    description:
      'Unified request patterns and data structures across the Python finance library, TypeScript clients, endpoints, and datasets.',
  },
];
// ─── Styled Components ────────────────────────────────────────────────────────

const Section = styled.section`
  background-color: #ffffff;
  padding: 80px 40px;
  padding-top: 160px;


  @media (max-width: 768px) {
    padding:  80px 20px;
  }
`;

const Header = styled.div`
  margin-bottom: 56px;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #9ca3af;
  margin: 0 0 12px 0;
`;

const Heading = styled.h2`
  font-size: clamp(32px, 4vw, 48px);
  color: #0f0f0f;
  margin: 0;
  line-height: 1.15;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid #e5e7eb;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  padding: 36px 32px 40px;
  border-bottom: 1px solid #e5e7eb;

  /* Right border on all but the last in each row */
  &:not(:nth-child(3n)) {
    border-right: 1px solid #e5e7eb;
  }

  @media (max-width: 900px) {
    &:not(:nth-child(3n)) {
      border-right: none;
    }
    &:not(:nth-child(2n)) {
      border-right: 1px solid #e5e7eb;
    }
  }

  @media (max-width: 560px) {
    &:not(:nth-child(2n)) {
      border-right: none;
    }
  }
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  margin-bottom: 16px;
`;

const CardTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 10px 0;
  letter-spacing: -0.01em;
`;

const CardDescription = styled.p`
  font-size: 14px;
  line-height: 1.65;
  color: #6b7280;
  margin: 0;
  max-width: 320px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const FeatureGrid: React.FC = () => {
  return (
    <Section>
      <Header>
        <Eyebrow>Capabilities</Eyebrow>
        <Heading className="serif">Financial data SDKs that fit your needs</Heading>
      </Header>

      <Grid>
        {features.map((feature) => (
          <Card key={feature.title}>
            <IconWrapper>{feature.icon}</IconWrapper>
            <CardTitle>{feature.title}</CardTitle>
            <CardDescription>{feature.description}</CardDescription>
          </Card>
        ))}
      </Grid>
    </Section>
  );
};

export default FeatureGrid;
